"use client";

import type { Translate } from "@/lib/i18n/translate";
import { useTranslations } from "next-intl";
import { type ReactNode, type RefObject, useEffect, useRef } from "react";

import { HeroDemoCursorIcon } from "./hero-demo-cursor-icon";

/** Chip labels toured by the demo cursor (prototype `_demoCursor`). */
function demoCursorChipLabels(t: Translate): readonly string[] {
  return ["Inbox", t("dokumente"), t("telefonanlage"), "Online-Meetings"];
}

const DEMO_RIPPLE_ATTR = "data-demo-ripple";
const DEMO_FLASH_CLASS = "mkhero-demo-cursor-chip-flash";

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function clearDemoRipples(wrap: HTMLElement): void {
  wrap
    .querySelectorAll<HTMLElement>(`[${DEMO_RIPPLE_ATTR}]`)
    .forEach((node) => {
      node.remove();
    });
}

function clearDemoFlash(wrap: HTMLElement): void {
  wrap.querySelectorAll<HTMLElement>("button").forEach((button) => {
    button.classList.remove(DEMO_FLASH_CLASS);
  });
}

/**
 * Prototype `_demoCursor` — glides between feature chips and simulates clicks.
 * Runs on every page load until the user clicks the chip row.
 */
export function HeroDemoCursor(props: {
  readonly rootRef: RefObject<HTMLElement | null>;
}): ReactNode {
  const t = useTranslations("marketing.heroDemoCursor");
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const aliveRef = useRef<boolean>(true);
  const ripplesRef = useRef<readonly HTMLDivElement[]>([]);

  useEffect((): (() => void) => {
    aliveRef.current = true;

    const wrap: HTMLElement | null = props.rootRef.current;
    const cur: HTMLDivElement | null = cursorRef.current;
    const svg: SVGSVGElement | null = svgRef.current;

    if (wrap === null || cur === null || svg === null) {
      return (): void => {
        aliveRef.current = false;
      };
    }

    clearDemoRipples(wrap);
    clearDemoFlash(wrap);

    const dead = (): boolean => !aliveRef.current || !cur.isConnected;

    const TIPX = 4;
    const TIPY = 3;

    const center = (el: HTMLElement): { x: number; y: number } => {
      const w: DOMRect = wrap.getBoundingClientRect();
      const r: DOMRect = el.getBoundingClientRect();
      return {
        x: r.left - w.left + r.width / 2 - TIPX,
        y: r.top - w.top + r.height / 2 - TIPY,
      };
    };

    const findTargets = (): HTMLElement[] => {
      const buttons: NodeListOf<HTMLElement> =
        wrap.querySelectorAll<HTMLElement>("button");
      const targets: HTMLElement[] = [];
      for (const label of demoCursorChipLabels(t)) {
        for (const button of buttons) {
          if (button.textContent?.trim() === label) {
            targets.push(button);
            break;
          }
        }
      }
      return targets;
    };

    const ripple = (x: number, y: number): void => {
      const rp: HTMLDivElement = document.createElement("div");
      rp.setAttribute(DEMO_RIPPLE_ATTR, "1");
      rp.className = "mkhero-demo-cursor-ripple";
      rp.style.left = `${String(x + TIPX)}px`;
      rp.style.top = `${String(y + TIPY)}px`;
      wrap.appendChild(rp);
      ripplesRef.current = [...ripplesRef.current, rp];

      window.requestAnimationFrame(() => {
        rp.classList.add("mkhero-demo-cursor-ripple-active");
      });

      window.setTimeout(() => {
        rp.remove();
        ripplesRef.current = ripplesRef.current.filter((node) => node !== rp);
      }, 640);
    };

    const flash = (el: HTMLElement): void => {
      el.classList.remove(DEMO_FLASH_CLASS);
      void el.offsetWidth;
      el.classList.add(DEMO_FLASH_CLASS);
      window.setTimeout(() => {
        el.classList.remove(DEMO_FLASH_CLASS);
      }, 430);
    };

    const moveTo = async (x: number, y: number, dur: number): Promise<void> => {
      cur.style.transition = `transform ${String(dur)}ms cubic-bezier(.45,.05,.2,1)`;
      cur.style.transform = `translate3d(${String(x)}px,${String(y)}px,0)`;
      await waitMs(dur);
    };

    const stop = (): void => {
      if (!aliveRef.current) return;
      aliveRef.current = false;
      cur.style.opacity = "0";
      clearDemoRipples(wrap);
      clearDemoFlash(wrap);
    };

    wrap.addEventListener("click", stop);

    const startTimeoutId: number = window.setTimeout(() => {
      const targets: HTMLElement[] = findTargets();
      if (targets.length === 0 || dead()) {
        stop();
        return;
      }

      let idx = 0;

      const loop = async (): Promise<void> => {
        const firstTarget: HTMLElement = targets[0]!;
        const p0 = center(firstTarget);
        cur.style.transform = `translate3d(${String(p0.x)}px,${String(p0.y + 30)}px,0)`;
        await waitMs(350);
        if (dead()) return;
        cur.style.opacity = "1";

        while (!dead()) {
          const t: HTMLElement = targets[idx % targets.length]!;
          const p = center(t);
          await moveTo(p.x, p.y, 720);
          if (dead()) break;
          svg.style.transition = "transform .13s ease";
          svg.style.transform = "scale(0.8)";
          window.setTimeout(() => {
            if (!dead()) {
              svg.style.transform = "scale(1)";
            }
          }, 140);
          ripple(p.x, p.y);
          flash(t);
          await waitMs(1000);
          if (dead()) break;
          idx += 1;
        }
      };

      void loop();
    }, 150);

    return (): void => {
      aliveRef.current = false;
      window.clearTimeout(startTimeoutId);
      wrap.removeEventListener("click", stop);
      clearDemoRipples(wrap);
      clearDemoFlash(wrap);
    };
  }, [props.rootRef]);

  return (
    <div
      ref={cursorRef}
      data-democursor="1"
      className="mkhero-demo-cursor"
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        zIndex: 30,
        pointerEvents: "none",
        opacity: 0,
        transition: "opacity 0.45s ease",
      }}
    >
      <HeroDemoCursorIcon ref={svgRef} />
    </div>
  );
}
